import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Star, TrendingUp, ThumbsUp, AlertTriangle, ChevronLeft, MessageSquare, FileDown, FileSpreadsheet, Building2, Users } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const COLORS = { positive: "#16a34a", neutral: "#FFD600", negative: "#FF3B30", brand: "#FF3B30", ink: "#0A0A0A", cyan: "#00E5FF" };
const SUBJECT_LABEL = { product: "Product", service: "Service", provider: "Provider", platform: "Platform" };

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/feedback/analytics").then(({ data }) => setData(data));
    api.get("/feedback/stats").then(({ data }) => setStats(data));
  }, []);

  const sentimentPie = useMemo(() => ([
    { name: "Positive", value: stats.by_sentiment?.positive || 0, color: COLORS.positive },
    { name: "Neutral", value: stats.by_sentiment?.neutral || 0, color: COLORS.neutral },
    { name: "Negative", value: stats.by_sentiment?.negative || 0, color: COLORS.negative },
  ]), [stats]);

  if (!data) return <div className="py-16 text-center text-sm text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/feedback" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
            <ChevronLeft size={12} /> Back to feedback log
          </Link>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Feedback analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">Descriptive analytics per product, service, provider &amp; platform.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${BACKEND_URL}/api/feedback/export.csv`} target="_blank" rel="noreferrer" data-testid="export-csv">
            <button className="btn-outline"><FileSpreadsheet size={14} /> Export CSV</button>
          </a>
          <a href={`${BACKEND_URL}/api/feedback/analytics.pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf">
            <button className="btn-primary"><FileDown size={14} /> Export PDF</button>
          </a>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Total entries" value={stats.total || 0} icon={MessageSquare} color={COLORS.ink} />
        <Tile label="Avg rating" value={(stats.avg_rating ?? 0).toFixed(1)} icon={Star} color={COLORS.neutral} />
        <Tile label="Positive share"
          value={`${percent(stats.by_sentiment?.positive, stats.total)}%`}
          icon={ThumbsUp} color={COLORS.positive} />
        <Tile label="Needs attention"
          value={`${percent(stats.by_sentiment?.negative, stats.total)}%`}
          icon={AlertTriangle} color={COLORS.brand} />
      </div>

      {/* Trend + Rating distribution */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Last 30 days · volume & avg rating" icon={TrendingUp}>
          {data.trend.length === 0 ? (
            <Empty msg="No feedback recorded in the last 30 days." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.trend} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke={COLORS.ink} strokeWidth={2} dot={{ r: 3 }} name="Entries" />
                <Line yAxisId="right" type="monotone" dataKey="avg_rating" stroke={COLORS.brand} strokeWidth={2} dot={{ r: 3 }} name="Avg rating" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Rating distribution" icon={Star}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.rating_distribution} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
              <XAxis dataKey="rating" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}★`} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="count" fill={COLORS.brand} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Sentiment pie + Sentiment by subject_type */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Overall sentiment mix" icon={ThumbsUp}>
          {(stats.total || 0) === 0 ? <Empty msg="No feedback yet." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={sentimentPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {sentimentPie.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Sentiment by subject type" icon={MessageSquare}>
          {data.sentiment_by_type.length === 0 ? <Empty msg="No data yet." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.sentiment_by_type} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="subject_type" tick={{ fontSize: 10 }} tickFormatter={(v) => SUBJECT_LABEL[v] || v} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="positive" stackId="a" fill={COLORS.positive} name="Positive" />
                <Bar dataKey="neutral" stackId="a" fill={COLORS.neutral} name="Neutral" />
                <Bar dataKey="negative" stackId="a" fill={COLORS.negative} name="Negative" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Per-subject-ref breakdown */}
      <Card title="Per product / service breakdown" icon={Star}>
        {data.per_subject.length === 0 ? (
          <Empty msg="Add feedback with a subject reference to see per-item breakdowns." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="per-subject-table">
              <thead>
                <tr className="border-b text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  <th className="p-2">Item</th>
                  <th className="p-2">Type</th>
                  <th className="p-2 text-right">Entries</th>
                  <th className="p-2 text-right">Avg</th>
                  <th className="p-2 w-64">Sentiment split</th>
                </tr>
              </thead>
              <tbody>
                {data.per_subject.map((r) => {
                  const total = r.positive + r.neutral + r.negative;
                  const p = total ? (r.positive / total) * 100 : 0;
                  const n = total ? (r.neutral / total) * 100 : 0;
                  const g = total ? (r.negative / total) * 100 : 0;
                  return (
                    <tr key={`${r.subject_ref}-${r.subject_type}`} className="border-b last:border-0">
                      <td className="p-2 font-semibold">{r.subject_ref}</td>
                      <td className="p-2"><span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">{SUBJECT_LABEL[r.subject_type]}</span></td>
                      <td className="p-2 text-right font-mono font-bold">{r.count}</td>
                      <td className="p-2 text-right font-mono font-bold">
                        {r.avg_rating.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span>
                      </td>
                      <td className="p-2">
                        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div style={{ width: `${p}%`, background: COLORS.positive }} title={`${r.positive} positive`} />
                          <div style={{ width: `${n}%`, background: COLORS.neutral }} title={`${r.neutral} neutral`} />
                          <div style={{ width: `${g}%`, background: COLORS.negative }} title={`${r.negative} negative`} />
                        </div>
                        <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
                          <span className="text-green-600">+{r.positive}</span>
                          <span className="text-brand-yellow">·{r.neutral}</span>
                          <span className="text-brand-orange">−{r.negative}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top / bottom lists */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Top rated" icon={ThumbsUp}>
          <RankList items={data.top_positive} accent={COLORS.positive} />
        </Card>
        <Card title="Needs attention" icon={AlertTriangle}>
          <RankList items={data.needs_attention} accent={COLORS.brand} />
        </Card>
      </div>

      {/* Voice-of-market insights (public landing feedback) */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Us vs external vendors" icon={Building2}>
          <VsCompare data={data.our_vs_external || {}} />
        </Card>
        <Card title="Feedback source split" icon={Users}>
          <OriginSplit data={data.by_origin || {}} total={stats.total} />
        </Card>
        <Card title="Top mentioned vendors" icon={Building2}>
          <VendorList items={data.top_vendors || []} />
        </Card>
      </div>
    </div>
  );
}

function VsCompare({ data }) {
  const ours = data.ours || { count: 0, avg_rating: 0, positive: 0, negative: 0 };
  const ext = data.external || { count: 0, avg_rating: 0, positive: 0, negative: 0 };
  if (ours.count === 0 && ext.count === 0) return <Empty msg="No comparative data yet." />;
  const rows = [
    { label: "Us (TridentDigitalPulse)", d: ours, accent: "#FF3B30" },
    { label: "External vendors", d: ext, accent: "#0A0A0A" },
  ];
  return (
    <div className="space-y-3" data-testid="us-vs-external">
      {rows.map((r) => (
        <div key={r.label} className="rounded-xl border p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest" style={{ color: r.accent }}>{r.label}</div>
            <div className="font-mono font-bold">{r.d.avg_rating.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span></div>
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{r.d.count} responses</div>
          <div className="mt-2 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div style={{ width: `${r.d.count ? (r.d.positive / r.d.count) * 100 : 0}%`, background: "#16a34a" }} />
            <div style={{ width: `${r.d.count ? (r.d.neutral / r.d.count) * 100 : 0}%`, background: "#FFD600" }} />
            <div style={{ width: `${r.d.count ? (r.d.negative / r.d.count) * 100 : 0}%`, background: "#FF3B30" }} />
          </div>
          <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
            <span className="text-green-600">+{r.d.positive}</span>
            <span>·{r.d.neutral}</span>
            <span className="text-brand-orange">−{r.d.negative}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function OriginSplit({ data, total }) {
  const pub = data.public || 0;
  const fr = data.franchisee || 0;
  const t = pub + fr;
  if (t === 0) return <Empty msg="No feedback yet." />;
  return (
    <div className="space-y-3" data-testid="origin-split">
      <Row label="Public (landing page)" count={pub} total={t} color="#FF3B30" />
      <Row label="Franchisee-collected" count={fr} total={t} color="#0A0A0A" />
      <div className="mt-2 rounded-xl bg-brand-yellow/10 p-3 text-xs">
        <b>{Math.round(pub / t * 100)}%</b> of feedback now arrives organically through the public landing form.
      </div>
    </div>
  );
}

function Row({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold">{label}</span>
        <span className="font-mono font-bold">{count} · {pct}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div style={{ width: `${pct}%`, background: color }} className="h-full" />
      </div>
    </div>
  );
}

function VendorList({ items }) {
  if (!items || items.length === 0) return <Empty msg="No external vendors mentioned yet." />;
  return (
    <ol className="space-y-2" data-testid="top-vendors">
      {items.map((v, i) => (
        <li key={v.vendor_name} className="flex items-center justify-between rounded-xl border p-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-ink text-[10px] font-bold text-white">{i + 1}</span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{v.vendor_name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{v.count} mentions</div>
            </div>
          </div>
          <div className="font-mono font-bold">{v.avg_rating.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span></div>
        </li>
      ))}
    </ol>
  );
}

function Tile({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-1 font-heading text-xl font-extrabold tracking-tight">{value}</div>
        </div>
        <div className="grid h-9 w-9 place-items-center rounded-xl text-white" style={{ background: color }}>
          <Icon size={14} />
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon size={14} className="text-brand-orange" />
        <div className="font-heading text-sm font-bold uppercase tracking-widest">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="grid h-[220px] place-items-center text-xs text-muted-foreground">{msg}</div>;
}

function RankList({ items, accent }) {
  if (!items || items.length === 0) return <Empty msg="No entries yet." />;
  return (
    <ol className="space-y-2">
      {items.map((r, i) => (
        <li key={`${r.subject_ref}-${r.subject_type}`} className="flex items-center justify-between rounded-xl border p-3">
          <div className="flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full text-xs font-bold text-white" style={{ background: accent }}>{i + 1}</span>
            <div>
              <div className="font-heading text-sm font-bold">{r.subject_ref}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{SUBJECT_LABEL[r.subject_type]} · {r.count} entries</div>
            </div>
          </div>
          <div className="font-mono font-bold">{r.avg_rating.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span></div>
        </li>
      ))}
    </ol>
  );
}

function percent(n, d) {
  if (!d) return 0;
  return Math.round(((n || 0) / d) * 100);
}
